import { useMemo, useState, type KeyboardEvent } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Button,
  ConditionViewerItem,
  ConditionViewerTerm,
  Icon,
  InlineStack,
  Tooltip,
} from '@customerio/pluma-components/react';
import {
  OperatorValue,
  PEOPLE_SEARCH_OPERATOR_FAMILIES,
  ProfileAttributeNamePicker,
  peopleGroupsForFamilies,
  toValueOptions,
  useProfileAttributeNames,
  isUnaryFilterOperator,
  type AttributeFilterState,
  type OperatorGroup,
} from '@/features/dryad';
import { useAttributeValues } from '@/services/db/attribute/queries';
import { recipientFilterErrorMessage, recipientFilterErrors } from '../utils/recipient-filters';

/**
 * The one profile-attribute condition a recipient search by filters asks
 * about: Ember's `condition-list/item` with `conditionTypes = ['attribute']`.
 *
 * While editing it is the people search's attribute row — the same name
 * picker, and only the equality and existence operators — with an Apply that
 * runs the search. The operators are Ember's own limit, not a narrowing: its
 * attribute editor outside the compact variation always passes
 * `limitedOperators` (equality + existence). The customers endpoint agrees:
 * in production a `contains` or `gt` row there is a 400.
 *
 * Once applied it reads back as the condition, which can be opened to
 * edit again or removed.
 */

type RecipientFilterConditionProps = {
  workspaceId: number;
  /** The condition as last applied, or the blank one before any has been. */
  condition: AttributeFilterState;
  isEditing: boolean;
  isDisabled: boolean;
  onApply: (condition: AttributeFilterState) => void;
  onEdit: () => void;
  onDelete: () => void;
};

export function RecipientFilterCondition({
  workspaceId,
  condition,
  isEditing,
  isDisabled,
  onApply,
  onEdit,
  onDelete,
}: RecipientFilterConditionProps) {
  if (isEditing) {
    return (
      <RecipientFilterEditor
        workspaceId={workspaceId}
        initialCondition={condition}
        isDisabled={isDisabled}
        onApply={onApply}
      />
    );
  }

  return (
    <InlineStack gap="050" alignBlock="start">
      <AppliedCondition condition={condition} isDisabled={isDisabled} onEdit={onEdit} />
      <Button
        aria-label="Remove condition"
        icon="close"
        isIconOnly
        isCritical
        variant="tertiary"
        size="sm"
        isDisabled={isDisabled}
        onClick={onDelete}
      />
    </InlineStack>
  );
}

function operatorGroups(field: string): OperatorGroup[] {
  return peopleGroupsForFamilies(PEOPLE_SEARCH_OPERATOR_FAMILIES, field);
}

/** The operator as the dropdown named it when it was chosen. */
function operatorLabel(condition: AttributeFilterState): string {
  const options = operatorGroups(condition.field).flatMap((group) => group.options);
  return options.find((option) => option.value === condition.operator)?.label ?? condition.operator;
}

type AppliedConditionProps = {
  condition: AttributeFilterState;
  isDisabled: boolean;
  onEdit: () => void;
};

/**
 * The condition read back as a sentence, as Ember's condition label reads it,
 * and clicked to edit it again as Ember's is.
 *
 * The viewer item is itself the button, by role rather than as a <button>:
 * Pluma draws a <div> inside it, which a <button> may not hold.
 */
function AppliedCondition({ condition, isDisabled, onEdit }: AppliedConditionProps) {
  const edit = () => {
    if (!isDisabled) {
      onEdit();
    }
  };
  return (
    <ConditionViewerItem
      role="button"
      tabIndex={0}
      aria-disabled={isDisabled}
      cursor={isDisabled ? undefined : 'pointer'}
      icon="person"
      onClick={edit}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          edit();
        }
      }}
    >
      <ConditionViewerTerm>{condition.field}</ConditionViewerTerm> {operatorLabel(condition)}
      {isUnaryFilterOperator(condition.operator) ? null : (
        <>
          {' '}
          {/* The value is subscriber data; keep it out of session replay. */}
          <ConditionViewerTerm className="fs-mask">{condition.value}</ConditionViewerTerm>
        </>
      )}
    </ConditionViewerItem>
  );
}

type RecipientFilterEditorProps = {
  workspaceId: number;
  initialCondition: AttributeFilterState;
  isDisabled: boolean;
  onApply: (condition: AttributeFilterState) => void;
};

function RecipientFilterEditor({ workspaceId, initialCondition, isDisabled, onApply }: RecipientFilterEditorProps) {
  const { hasPermission } = usePermissions();
  const canViewAttributeValues = hasPermission('journeys/attribute_data/view');
  const [draft, setDraft] = useState(initialCondition);

  // The workspace the caller resolved, not the URL's: Activity Logs opens this
  // modal from `/workspaces/last/...`, where the URL holds no numeric id.
  const names = useProfileAttributeNames(draft.field, workspaceId);
  // A blank field makes no request, which is how a viewer who may not read
  // attribute data is asked nothing on their behalf.
  const values = useAttributeValues(workspaceId, canViewAttributeValues ? draft.field : '');
  const valueOptions = useMemo(() => toValueOptions(values.data), [values.data]);

  const errors = recipientFilterErrors(draft);
  const errorMessage = recipientFilterErrorMessage(errors);

  return (
    <InlineStack gap="050" alignBlock="baseline" wrap>
      {/* Lower-case placeholders, as Ember's attribute condition editor has
          them in this modal (`condition-list/item`). */}
      <ProfileAttributeNamePicker
        placeholder="profile attribute"
        field={draft.field}
        isDisabled={isDisabled}
        names={names}
        onFieldChange={(field) => setDraft((current) => ({ ...current, field }))}
        onTouched={() => {}}
      />
      <OperatorValue
        field={draft.field}
        operator={draft.operator}
        value={draft.value}
        value2={draft.value2}
        operatorGroups={operatorGroups(draft.field)}
        valueOptions={valueOptions}
        isLoadingValueOptions={values.isFetching}
        disabled={isDisabled}
        valuePlaceholder="value"
        onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
      />
      {/* Apply and cancel, 8px apart, as Ember's condition editor draws
          them (`condition-list/edit`): each a small button holding its icon.
          Apply is disabled while the condition is incomplete, and the
          tooltip beside it names what is missing, so the flip is what says
          which parts are required. The cancel is always disabled here, as
          this modal passes `isCancelDisabled`. */}
      <InlineStack gap="100">
        <Tooltip isDisabled={errors.length === 0} content={errorMessage} isCustom>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              aria-label="Apply condition"
              variant="primary"
              size="sm"
              isDisabled={isDisabled || errors.length > 0}
              onClick={() => onApply(draft)}
            >
              <Icon name="check" />
            </Button>
          )}
        </Tooltip>
        <Button
          aria-label="Cancel condition"
          variant="tertiary"
          size="sm"
          isDisabled
          onClick={() => setDraft(initialCondition)}
        >
          <Icon name="close" />
        </Button>
      </InlineStack>
    </InlineStack>
  );
}
